import {
  Typography,
  Divider,
  Stack,
  TextField,
  Button,
  Avatar,
  Box,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import imageFileToBase64 from "../../../../../hooks/imageToBase64";
import { useToast } from "../../../../shared/toastMessages/toastProvider";

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "15px",
  boxShadow: "1px 5px 5px 1px rgba(0,0,0,0.12)",
  p: { xs: 2, md: 3 },
};

const EditMailingInfo = ({ theme, mailingInfo, onSave }) => {
  const [avatar, setAvatar] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [initialData, setInitialData] = useState({});

  useEffect(() => {
    if (mailingInfo) {
      const data = {
        avatar: mailingInfo.icon || "",
        name: mailingInfo.list_name || "",
        description: mailingInfo.description || "",
      };
      setAvatar(data.avatar);
      setName(data.name);
      setDescription(data.description);
      setInitialData(data);
    }
  }, [mailingInfo]);

  const hasChanges = useMemo(() => {
    return (
      avatar !== initialData.avatar ||
      name !== initialData.name ||
      description !== initialData.description
    );
  }, [avatar, name, description, initialData]);

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadingAvatar(true);
    try {
      const base64 = await imageFileToBase64(file, 200);
      setAvatar(base64);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Unable to process image.", "error");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    if (!hasChanges || !mailingInfo || saving) return;

    const updatedMailing = {
      id: mailingInfo.id,
      list_name: name.trim(),
      description: description.trim(),
      icon: avatar,
      time_created: mailingInfo.time_created,
    };

    setSaving(true);
    try {
      if (onSave) await onSave(updatedMailing);
    } finally {
      setSaving(false);
    }
  };

  const handleNav = () => {
    const lastUrl = localStorage.getItem("lastMailingListUrl");
    if (lastUrl) {
      navigate(lastUrl);
      localStorage.removeItem("lastMailingListUrl");
    } else {
      navigate("/Portal/Mailing/Manage");
    }
  };

  if (!mailingInfo) {
    return (
      <Paper elevation={0} sx={cardSx}>
        <Box
          sx={{
            py: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <CircularProgress color="primary" size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading mailing list details...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={cardSx}>
      <Button
        variant="text"
        onClick={handleNav}
        startIcon={<ChevronLeftIcon />}
        sx={{
          alignSelf: "flex-start",
          color: "black",
          textTransform: "none",
          fontWeight: 600,
          px: 0,
          mb: 1.5,
        }}
      >
        Back
      </Button>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <ListAltOutlinedIcon fontSize="small" color="action" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              List details
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Update the name, description, and icon for this mailing list.
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!hasChanges || saving || !name.trim()}
          theme={theme}
          startIcon={<SaveOutlinedIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            px: 2.5,
            alignSelf: { xs: "stretch", sm: "center" },
          }}
        >
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        alignItems={{ xs: "stretch", md: "flex-start" }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: 220 },
            flexShrink: 0,
            p: 2,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "#fafafa",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Avatar
            src={avatar}
            alt={name || "Mailing list"}
            sx={{
              width: 96,
              height: 96,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "#fff",
              color: "text.secondary",
              fontSize: 36,
              fontWeight: 600,
            }}
          >
            {(name || "M").charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: "center" }}>
            List icon
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", mb: 0.5 }}
          >
            Optional image shown with this list.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            size="small"
            startIcon={<PhotoCameraOutlinedIcon />}
            disabled={uploadingAvatar}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
            }}
            theme={theme}
          >
            {uploadingAvatar ? "Processing..." : "Upload icon"}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleAvatarChange}
            />
          </Button>
        </Box>

        <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Mailing list name
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              placeholder="e.g. Q1 product leads"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Description
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              multiline
              minRows={4}
              placeholder="What is this list used for?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
              }}
            />
          </Box>

          {hasChanges ? (
            <Typography variant="caption" color="text.secondary">
              You have unsaved changes.
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No changes to save.
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default EditMailingInfo;
